import { createCallTokenRequest } from "@ano/shared/api/calls";
import { API_URL } from "./config";

export type CallMode = "audio" | "video";

/** Full token payload returned by the call-token route. The new
 *  in-app native call surface uses `{ serverUrl, token }` directly via
 *  the LiveKit JS SDK; `joinUrl` stays in the shape so the
 *  "Open in browser" fallback button can still escape to the hosted
 *  page if the in-app surface fails to connect. */
export interface ChannelCallToken {
  serverUrl: string;
  token: string;
  joinUrl: string;
  roomName: string;
  expiresAt: number;
  mode: CallMode;
}

export async function createChannelCallToken({
  workspaceId,
  channelId,
  mode,
  accessToken,
}: {
  /** Optional. Workspace channels send the workspace id for an extra
   *  belt-and-suspenders check; DMs from Home view can omit it
   *  entirely (channel membership alone gates auth). */
  workspaceId?: string | null;
  channelId: string;
  mode: CallMode;
  accessToken: string;
}): Promise<ChannelCallToken> {
  const response = await createCallTokenRequest({
    apiBaseUrl: API_URL,
    accessToken,
    body: {
      ...(workspaceId ? { workspaceId } : {}),
      channelId,
      mode,
    },
  });
  return {
    serverUrl: response.serverUrl,
    token: response.token,
    joinUrl: response.joinUrl,
    roomName: response.roomName,
    expiresAt: response.expiresAt,
    mode: response.mode,
  };
}

/**
 * @deprecated Use `createChannelCallToken` so callers can drive the
 * native in-app surface (which needs `{ serverUrl, token }`) and keep
 * the browser-fallback `joinUrl` independently. Kept until the last
 * caller migrates.
 */
export async function createChannelCallJoinUrl(args: {
  workspaceId: string;
  channelId: string;
  mode: CallMode;
  accessToken: string;
}): Promise<string> {
  const tok = await createChannelCallToken(args);
  return tok.joinUrl;
}

export async function warmCallMediaPermissions(
  mode: "audio" | "video",
): Promise<boolean> {
  if (typeof navigator === "undefined") return true;
  if (!navigator.mediaDevices?.getUserMedia) return true;

  const constraints: MediaStreamConstraints =
    mode === "video"
      ? { audio: true, video: true }
      : { audio: true, video: false };

  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    for (const track of stream.getTracks()) {
      track.stop();
    }
    return true;
  } catch {
    return false;
  }
}
